import { useTranslation } from "@i18n";
import React from "react";
import IconComponent from "@CubeComponents/icons";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Accordion from "@CubeComponents/html/accordion/materialUiAccordian/index";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import InPlaceEditorComponent from "@CubeComponents/Mui_Form/InPlaceEditorComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { PlusIcon } from "@Components/StickySidebarViewer/assets/selectionIcons";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import AddUser from "../CreateInspectionAndProgress/addUserModal";
import FormPreview from "@Features/portal-settings/components/PortalSetings/Configuration/FormsAndFields/FormTemplate/formPreview";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { Box, Chip } from "@mui/material";
import Text from "@CubeComponents/html/Text/Text";
import { getSectionName } from "../CreateInspectionAndProgress/constants";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import useFormFlowCreation, { type FormFlowTemplate } from "./useFormFlowCreation";
import {
  ACTION_BTN_TEXT_MAX_LENGTH,
  FORM_FLOW_SOURCES,
  LEVEL_HEADING_MAX_LENGTH,
  NON_DELETABLE_ACTION_IDS,
} from "./formFlow.constants";
import type {
  FormFlowActionStep,
  FormFlowLevel,
  FormFlowMenuItem,
  FormFlowSection,
  FormFlowStep,
  FormFlowUser,
} from "./formFlow.types";

interface FormFlowCreationNewProps {
  /** Existing flow to load and update. `null` seeds a new flow from `selectedTemplate`. */
  formFlowId?: string | null;
  projectId?: string;
  /** Leaves the editor — also called after a successful save. */
  handleBackClick?: () => void;
  /** Read-only render: no header, no edit/save buttons. */
  previewMode?: boolean;
  /** Template a brand-new flow is seeded from. */
  selectedTemplate?: FormFlowTemplate;
  /** Which screen embeds the editor — see `FORM_FLOW_SOURCES`. */
  source?: string;
  /** Cancels the embedding form-creation step (FORM_CREATION source only). */
  handleCancelFormAttachment?: () => void;
  /** Hands the built levels to the embedding form-creation step. */
  handleCreateClick?: (levels: FormFlowLevel[]) => void;
  showLoader?: boolean;
  /** Space the flow belongs to. Falls back to the loaded flow's own space. */
  spaceId?: string;
  /** Edit a single level in isolation and report it back through `onLevelChange`. */
  singleLevelEditMode?: boolean;
  /** Levels to seed from in single-level edit mode. */
  formFlow?: FormFlowLevel[] | null;
  /** Index of the level being edited in single-level edit mode. */
  editLevelIndex?: number | null;
  /** Called with the edited level (edit flags stripped) and whether it is saveable. */
  onLevelChange?: (level: FormFlowLevel, isSaveable: boolean) => void;
}

function FormFlowCreationNew({
  formFlowId = null,
  projectId,
  handleBackClick = () => {},
  previewMode = false,
  selectedTemplate = {},
  source = "",
  handleCancelFormAttachment = () => {},
  handleCreateClick = () => {},
  showLoader = false,
  spaceId,
  singleLevelEditMode = false,
  formFlow = null,
  editLevelIndex = null,
  onLevelChange = () => {},
}: FormFlowCreationNewProps) {
  const { t } = useTranslation();

  const {
    formFlowLevels,
    formFlowData,
    updatedTemplate,
    editMode,
    setEditMode,
    saveBtnState,
    addUserQuery,
    getLevelActionSteps,
    selectableSections,
    editedLevel,
    levelMenuItems,
    addStepMenuItems,
    addActionMenuItems,
    stepMenuItems,
    actionMenuItems,
    modalState,
    closeAddModal,
    openSectionsModal,
    openStepUsersModal,
    openActionUsersModal,
    allSelectedSections,
    handleSectionSelection,
    openNewFlowSaveModal,
    openSaveFlowModal,
    handleCloseModalClick,
    handleFlowNameChange,
    handleOptionSelect,
    handleAddUsersClick,
    handleAddSectionsClick,
    handleAddLevelClick,
    handleConditionChange,
    handleActionBtnEdit,
    saveNewBtnName,
    cancelBtnNameChange,
    saveNewHeaderName,
    handleEditHeaderCancelClick,
    removeSection,
    removeUser,
    handleSave,
    handleSaveFlowClick,
    handleCancelEditClick,
  } = useFormFlowCreation({
    formFlowId,
    projectId,
    spaceId,
    selectedTemplate,
    source,
    singleLevelEditMode,
    formFlow,
    editLevelIndex,
    onLevelChange,
    handleBackClick,
  });

  const header = () => {
    return (
      <div className="flex items-center gap-2 border-button_light border-b h-20 w-full">
        <div>
          <IconComponent
            name="ArrowLeft"
            width="1.5rem"
            height="1.5rem"
            className="cursor-pointer"
            onClick={handleBackClick}
          />
        </div>
        <div className="flex flex-col gap-1">
          <div className="font-semibold text-xl">{formFlowData.name}</div>
          <div className="text-sm">{updatedTemplate?.name}</div>
        </div>
      </div>
    );
  };

  const actionBtnEdit = ({
    level,
    actionStep,
    levelIndex,
  }: {
    level: FormFlowLevel;
    actionStep: FormFlowActionStep;
    levelIndex: number;
  }) => {
    return (
      <InPlaceEditorComponent
        mode="Inline"
        id={`${levelIndex}-${actionStep.btnTxtField}`}
        // The four button-caption keys all hold strings; `btnTxtField` is chosen at runtime, so the
        // read goes through the level's open index signature and lands on `unknown`.
        value={level[actionStep.btnTxtField] as string}
        enableEditMode
        actionSuccess={(args) => saveNewBtnName({ args, actionStep, levelIndex })}
        cancelClick={() => cancelBtnNameChange({ actionStep, levelIndex })}
        model={{ placeholder: "Type Here" }}
        type="Text"
        validationRules={{ Text: { required: true, maxLength: ACTION_BTN_TEXT_MAX_LENGTH } }}
        name="Text"
      />
    );
  };

  const levelHeader = ({ level, levelIndex }: { level: FormFlowLevel; levelIndex: number }) => {
    const showDefaultChip = levelIndex == 0 || levelIndex == formFlowLevels.length - 1;
    return (
      <div className="justify-between w-full p-3 border-b border-stroke_primary_light flex items-center">
        <div className="font-semibold flex items-center">
          {level.headingEditMode ? (
            <Box onClick={(e: React.SyntheticEvent) => e.stopPropagation()}>
              <InPlaceEditorComponent
                mode="Inline"
                enableEditMode
                value={level.heading}
                actionSuccess={(args) => saveNewHeaderName({ args, levelIndex })}
                cancelClick={() => handleEditHeaderCancelClick(levelIndex)}
                model={{ placeholder: "Type Here" }}
                type="Text"
                validationRules={{ Text: { required: true, maxLength: LEVEL_HEADING_MAX_LENGTH } }}
                name="Text"
              />
            </Box>
          ) : (
            <div className="flex items-center gap-4">
              {level.heading}
              {showDefaultChip && (
                <Chip
                  size="small"
                  sx={{
                    fontWeight: 400,
                    background: "#666a84",
                    color: "white",
                  }}
                  label="Default"
                />
              )}
            </div>
          )}
        </div>
        <div className="flex gap-3 items-center">
          <IconComponent name="arrowDown" width="1rem" height="1rem" />
          {editMode && (
            <Box onClick={(e) => e.stopPropagation()}>
              <SelectDropDown
                select={(args) => handleOptionSelect({ args, levelIndex })}
                cssClass="e-caret-hide"
                bgColor="transparent"
                items={levelMenuItems(levelIndex)}
              >
                <IconComponent name="threedots" width="1.25rem" height="1.25rem" className="cursor-pointer" />
              </SelectDropDown>
            </Box>
          )}
        </div>
      </div>
    );
  };

  const stepHeader = ({
    stepData,
    stepIndex,
    levelIndex,
  }: {
    stepData: FormFlowStep;
    stepIndex: number;
    levelIndex: number;
  }) => {
    return (
      <div className="justify-between w-full p-2 border-b border-stroke_primary_light flex items-center">
        <div className="flex items-center font-semibold">{stepData.type}</div>
        {editMode && (
          <Box onClick={(e) => e.stopPropagation()}>
            <SelectDropDown
              select={(args) => handleOptionSelect({ args, levelIndex, stepIndex })}
              cssClass="e-caret-hide"
              bgColor="transparent"
              items={stepMenuItems}
            >
              <IconComponent name="threedots" width="1.25rem" height="1.25rem" className="cursor-pointer" />
            </SelectDropDown>
          </Box>
        )}
      </div>
    );
  };

  const stepContent = ({
    stepData,
    stepIndex,
    levelIndex,
  }: {
    stepData: FormFlowStep;
    stepIndex: number;
    levelIndex: number;
  }) => {
    return (
      <div className="flex gap-4 flex-col w-full">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <Text>{t("Sections")}</Text>
            {editMode && (
              <div>
                <ButtonComponent
                  sx={{ padding: "0.375rem" }}
                  onClick={() => openSectionsModal({ stepData, levelIndex, stepIndex })}
                >
                  <IconComponent name="add" width="1.25rem" height="1.25rem" />
                </ButtonComponent>
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {stepData.sections.map((section: FormFlowSection) => (
              <div
                key={section._id}
                className="border items-center rounded flex gap-2 border-stroke_primary_light px-2 py-1"
              >
                {getSectionName(section)}
                {editMode && (
                  <IconComponent
                    onClick={() => removeSection({ sectionId: section._id, levelIndex, stepIndex })}
                    className="cursor-pointer"
                    name="cross"
                    width="1rem"
                    height="1rem"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="border-t border-stroke_primary_light" />
        <div className="flex flex-col gap-4">
          <div className="flex gap-4 items-center">
            <Text>{t("Users")}</Text>
            {editMode && (
              <div>
                <ButtonComponent
                  sx={{ padding: "0.375rem" }}
                  onClick={() => openStepUsersModal({ stepData, levelIndex, stepIndex })}
                >
                  <IconComponent name="UserProfile" width="1.25rem" height="1.25rem" />
                </ButtonComponent>
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {stepData.users.map((user: FormFlowUser) =>
              editMode ? (
                <UserNameTagComponent
                  key={user.id}
                  handleDeleteIconClicked={() => removeUser({ userId: user.id, levelIndex, stepIndex })}
                  id={user.id}
                />
              ) : (
                <UserNameTagComponent key={user.id} id={user.id} />
              ),
            )}
          </div>
        </div>
      </div>
    );
  };

  // `level` is in the parameter TYPE but deliberately not destructured: `getActions` passes the
  // same bag to `actionHeader` and `actionContent`, and only the latter reads it. Naming it here
  // would emit a binding this function does not use.
  const actionHeader = ({
    actionStep,
    levelIndex,
  }: {
    actionStep: FormFlowActionStep;
    levelIndex: number;
    level: FormFlowLevel;
  }) => {
    return (
      <div className="justify-between w-full p-2 border-b border-stroke_primary_light flex items-center">
        <div className="flex items-center font-semibold">{actionStep.name}</div>
        {editMode && !NON_DELETABLE_ACTION_IDS.includes(actionStep.id) && (
          <Box onClick={(e) => e.stopPropagation()}>
            <SelectDropDown
              select={(args) =>
                handleOptionSelect({
                  args,
                  levelIndex,
                  actionStep,
                })
              }
              cssClass="e-caret-hide"
              bgColor="transparent"
              items={actionMenuItems}
            >
              <IconComponent name="threedots" width="1.25rem" height="1.25rem" className="cursor-pointer" />
            </SelectDropDown>
          </Box>
        )}
      </div>
    );
  };

  const actionContent = ({
    actionStep,
    levelIndex,
    level,
  }: {
    actionStep: FormFlowActionStep;
    levelIndex: number;
    level: FormFlowLevel;
  }) => {
    return (
      <div className="flex gap-4 flex-col w-full">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <Text>{t("Display Button")}</Text>
            {editMode && (
              <div>
                <ButtonComponent
                  sx={{ padding: "0.375rem" }}
                  onClick={() => handleActionBtnEdit({ levelIndex, actionStep })}
                >
                  <IconComponent name="editProfile" width="1.25rem" height="1.25rem" />
                </ButtonComponent>
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {level[actionStep.btnTxtEditMode] ? (
              actionBtnEdit({ level, actionStep, levelIndex })
            ) : (
              <ButtonComponent>{level[actionStep.btnTxtField] as string}</ButtonComponent>
            )}
          </div>
        </div>
        <div className="border-t border-stroke_primary_light" />
        <div className="flex flex-col gap-4">
          <div className="flex gap-4 items-center">
            <Text>{t("Users")}</Text>
            {editMode && (
              <div>
                <ButtonComponent
                  sx={{ padding: "0.375rem" }}
                  onClick={() => openActionUsersModal({ level, levelIndex, actionStep })}
                >
                  <IconComponent name="UserProfile" width="1.25rem" height="1.25rem" />
                </ButtonComponent>
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* `usersField` is a runtime-chosen level key, so it resolves through the level's open
                index signature (`unknown`); the four keys it can name all hold user lists. The
                existing `?.` is what makes the read safe and is left exactly as it was. */}
            {(level[actionStep.usersField] as FormFlowUser[] | undefined)?.map((user: FormFlowUser) => (
              <UserNameTagComponent
                key={user.id}
                handleDeleteIconClicked={
                  editMode ? () => removeUser({ userId: user.id, levelIndex, actionStep }) : undefined
                }
                id={user.id}
              />
            ))}
          </div>
        </div>
      </div>
    );
  };

  const getSteps = ({ dataSource, levelIndex }: { dataSource: FormFlowStep[]; levelIndex: number }) => {
    return (
      <div key={levelIndex} className="flex flex-col gap-4 items-center">
        {dataSource.map((stepData: FormFlowStep, stepIndex: number) => (
          <Accordion
            defaultExpanded
            border="1px solid var(--stroke-primary)"
            style={{ width: "100%" }}
            borderRadius="0.625rem"
            key={`${stepData.type}-${stepIndex}`}
          >
            <AccordionSummary>{stepHeader({ stepData, stepIndex, levelIndex })}</AccordionSummary>
            <AccordionDetails>{stepContent({ stepData, stepIndex, levelIndex })}</AccordionDetails>
          </Accordion>
        ))}
      </div>
    );
  };

  /**
   * "Add step" / "Add action" dropdown. Which entries are disabled comes from the shared
   * constants; `beforeOpen` re-applies it at open time, which is when Syncfusion reads it.
   */
  const getStep_ActionOptions = ({ items, levelIndex }: { items: FormFlowMenuItem[]; levelIndex: number }) => {
    return (
      <SelectDropDown
        select={(args) => handleOptionSelect({ args, levelIndex })}
        cssClass="e-caret-hide"
        bgColor="transparent"
        items={items}
        beforeOpen={(args) => {
          items.forEach((item, index) => {
            if (args.items?.[index]) args.items[index].disabled = !!item.disabled;
          });
        }}
      >
        <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}>
          <span className="flex items-center gap-1">
            <IconComponent name="add" width="1rem" height="1rem" />
            <Text>{t("Add")}</Text>
            <IconComponent name="arrowDownSm" width="1rem" height="1rem" />
          </span>
        </ButtonComponent>
      </SelectDropDown>
    );
  };

  const getActions = ({ level, levelIndex }: { level: FormFlowLevel; levelIndex: number }) => {
    return (
      <div key={levelIndex} className="flex flex-col gap-4 w-full">
        {getLevelActionSteps(level).map((actionStep: FormFlowActionStep) => (
          <Accordion
            defaultExpanded
            key={actionStep.id}
            border="1px solid var(--stroke-primary)"
            style={{ width: "100%" }}
            borderRadius="0.625rem"
          >
            <AccordionSummary>{actionHeader({ actionStep, level, levelIndex })}</AccordionSummary>
            <AccordionDetails>{actionContent({ actionStep, level, levelIndex })}</AccordionDetails>
          </Accordion>
        ))}
        {levelIndex != 0 && !singleLevelEditMode && (
          <div className="flex flex-col gap-3 w-full border-t border-stroke_primary_light pt-4">
            <Text className="font-semibold">{t("Conditions")}</Text>
            <div className="flex items-center gap-2 w-full border border-stroke_primary_light rounded-lg p-3">
              <CheckBoxComponent
                enabled={editMode}
                checked={!!level.allowDynamicUserConfig}
                onChange={(_args: unknown, checked: boolean) =>
                  handleConditionChange({ levelIndex, field: "allowDynamicUserConfig", value: checked })
                }
              />
              <Text noWrap>{t("Allow dynamic user configuration at previous level submission")}</Text>
            </div>
          </div>
        )}
      </div>
    );
  };

  const levelContent = ({ level, levelIndex }: { level: FormFlowLevel; levelIndex: number }) => {
    return (
      <div className="flex">
        <div className="flex-1 flex flex-col gap-3 p-3">
          <div className="flex items-center gap-3">
            <Text className="font-semibold">{t("Steps")}</Text>
            {editMode && getStep_ActionOptions({ levelIndex, items: addStepMenuItems(levelIndex) })}
          </div>
          <div>{getSteps({ dataSource: level.dataSource, levelIndex })}</div>
        </div>
        <div className="border-s border-stroke_primary_light" />
        <div className="flex-1 flex flex-col gap-3 p-3">
          <div className="flex items-center gap-3">
            <Text className="font-semibold">{t("Actions")}</Text>
            {editMode && getStep_ActionOptions({ levelIndex, items: addActionMenuItems({ level, levelIndex }) })}
          </div>
          <div>{getActions({ level, levelIndex })}</div>
        </div>
      </div>
    );
  };

  const formFlowContent = () => {
    return (
      <div className="overflow-auto w-full flex flex-col gap-4">
        {formFlowLevels.map((level: FormFlowLevel, levelIndex: number) => (
          <Accordion
            key={level.heading}
            style={{ width: "100%" }}
            defaultExpanded
            border="1px solid var(--stroke-primary)"
            borderRadius="0.625rem"
          >
            <AccordionSummary>{levelHeader({ level, levelIndex })}</AccordionSummary>
            <AccordionDetails sx={{ padding: "0 !important" }}>{levelContent({ level, levelIndex })}</AccordionDetails>
          </Accordion>
        ))}
      </div>
    );
  };

  const headerBtns = () =>
    editMode ? (
      <div className="flex gap-4">
        {source != FORM_FLOW_SOURCES.FORM_CREATION && (
          <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} disabled={!saveBtnState} onClick={handleSave}>
            <Text>{t("Save")}</Text>
          </ButtonComponent>
        )}
        <ButtonComponent onClick={() => handleAddLevelClick()} buttonType="backgroundButton" disabled={!editMode}>
          <span className="flex items-center gap-1">
            <PlusIcon color={!editMode ? "var(--system-colour-37)" : "var(--primary-text)"} />
            <Text>{t("Add Level")}</Text>
          </span>
        </ButtonComponent>

        <ButtonComponent disabled={!editMode} onClick={handleCancelEditClick}>
          <Text>{t("Cancel")}</Text>
        </ButtonComponent>
      </div>
    ) : (
      <div>
        <ButtonComponent disabled={editMode} onClick={() => setEditMode(true)}>
          <div className="flex items-center gap-1">
            <IconComponent
              className={editMode ? "opacity-40" : ""}
              name="editProfile"
              width="1.25rem"
              height="1.25rem"
            />
            <Text>{t("Edit")}</Text>
          </div>
        </ButtonComponent>
      </div>
    );

  const addSectionHeader = () => {
    return (
      <div className="  w-full flex justify-between rounded-md">
        <Text>{t("Add Sections")}</Text>
      </div>
    );
  };

  const addSectionContent = () => {
    return (
      <div className="w-full overflow-auto">
        {selectableSections.length ? (
          <FormPreview
            fullWidthInput
            showHeaderCheckBox
            allowBorder
            disabled
            defaultExpanded={false}
            handleSectionSelection={handleSectionSelection}
            selectedSections={allSelectedSections}
            formData={{ template: selectableSections }}
          />
        ) : (
          <div className="flex flex-col gap-3 items-center justify-center w-full h-full">
            <IconComponent name="InspectionFormNoData" width="18.75rem" height="18.75rem" />
            <Text>{t("No section available to add")}</Text>
          </div>
        )}
      </div>
    );
  };

  const addSectionFooter = () => (
    <div className="flex gap-4 items-center">
      <ButtonComponent disabled={allSelectedSections.length == 0} onClick={handleAddSectionsClick}>
        <Text>{t("Add")}</Text>
      </ButtonComponent>
      <ButtonComponent onClick={closeAddModal}>
        <Text>{t("Cancel")}</Text>
      </ButtonComponent>
    </div>
  );

  const addFlowHeader = () => (
    <div className="  w-full flex justify-between rounded-md">
      <div className="flex gap-3 items-center justify-center">
        <div className="flex flex-col gap-1">
          <Text>{t("Save Form Flow")}</Text>
        </div>
      </div>
    </div>
  );

  const addFlowContent = () => (
    <div className=" flex flex-col gap-1">
      <div className="flex flex-col gap-4">
        <Text>{t("Form Flow Name")}</Text>
        <span>
          <TextFieldComponent
            name="name"
            type="text"
            placeholder={t("Add form flow name")}
            showClearButton
            onChange={handleFlowNameChange}
            value={openNewFlowSaveModal.title}
          />
        </span>
      </div>
    </div>
  );

  const showAddFlowHeader = () => {
    return (
      <div className="flex pb-5 gap-4 items-center">
        <span className="flex gap-3 items-center">
          <Text className="whitespace-normal">
            {t(
              "You are creating a new form flow which will only apply for the current case. You can also save this for re-use if you’d like.",
            )}
          </Text>
          <span>
            <ButtonComponent
              disabled={!saveBtnState}
              onClick={openSaveFlowModal}
              buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
            >
              <Text>{t("Save Form Flow")}</Text>
            </ButtonComponent>
          </span>
        </span>
      </div>
    );
  };

  const getFooterBtns = () => {
    return (
      <div className="mt-6 me-6 mb-6 ms-0 flex gap-4">
        <ButtonComponent
          disabled={!saveBtnState}
          buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          loading={showLoader}
          onClick={() => handleCreateClick(formFlowLevels)}
        >
          <Text>{t("Create")}</Text>
        </ButtonComponent>
        <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={handleCancelFormAttachment}>
          <Text>{t("Cancel")}</Text>
        </ButtonComponent>
      </div>
    );
  };

  const showSaveDisclaimer = () => (
    <div className="p-2 rounded-sm bg-banner_color_yellow/40 font-normal text-sm text-black content-center">
      <Text>
        {t(
          "Please ensure that all sections have fill permissions assigned and each action includes at least one user to proceed with saving.",
        )}
      </Text>
    </div>
  );

  // Add-users / add-sections popups are shared between the full editor and the single-level modal.
  const renderSharedModals = () => (
    <>
      {modalState.modalName == "users" && (
        <AddUser
          customQuery={addUserQuery}
          currentUsers={modalState.currentUsers}
          closeUsersModal={closeAddModal}
          handleAddUserClick={handleAddUsersClick}
        />
      )}
      {modalState.modalName == "sections" && (
        <ModalStateDialogComponent
          allowModalPadding={false}
          footerContent={addSectionFooter()}
          width="70%"
          height="100%"
          headerTitle={addSectionHeader()}
          onCancel={closeAddModal}
          isVisible
          modelContent={addSectionContent()}
        />
      )}
    </>
  );

  // ----- Single-level edit mode -----
  if (singleLevelEditMode) {
    return (
      <div className="w-full">
        {editedLevel && (
          <div className="p-4 flex flex-col gap-4">
            {!saveBtnState && showSaveDisclaimer()}
            {/* `editedLevel` is only non-null when `editLevelIndex != null` (see `useFormFlowCreation`),
                so the index is present in this branch. Erased at emit. */}
            {levelContent({ level: editedLevel, levelIndex: editLevelIndex as number })}
          </div>
        )}
        {renderSharedModals()}
      </div>
    );
  }

  return (
    <div className="flex p-4 gap-4 h-full flex-col overflow-auto w-full">
      {!(source == FORM_FLOW_SOURCES.FORM_CREATION || previewMode) && header()}
      {source == FORM_FLOW_SOURCES.FORM_CREATION && showAddFlowHeader()}
      {!previewMode && headerBtns()}
      {!saveBtnState && showSaveDisclaimer()}
      {formFlowContent()}
      {source == FORM_FLOW_SOURCES.FORM_CREATION && getFooterBtns()}
      {renderSharedModals()}
      {openNewFlowSaveModal.status && (
        <ModalStateDialogComponent
          allowModalPadding={false}
          footerContent={
            <div className="">
              <ButtonComponent
                disabled={openNewFlowSaveModal.title.trim() == ""}
                onClick={handleSaveFlowClick}
                buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
              >
                <Text>{t("Save")}</Text>
              </ButtonComponent>{" "}
            </div>
          }
          width="30%"
          headerTitle={addFlowHeader()}
          onCancel={handleCloseModalClick}
          isVisible={openNewFlowSaveModal.status}
          modelContent={addFlowContent()}
        />
      )}
    </div>
  );
}
export default FormFlowCreationNew;
